import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, RefreshCw } from 'lucide-react';
import {
  clearAuthAndTenant,
  fetchOrganizationsForAdmin,
  setOrganizationAccountHoldRemote,
  type OrganizationPublic,
  PRODUCT_NAME,
} from '../lib/tenant';
import { adminApi, type AdminOrgUsage, type AdminTenantUsageSummary, type PlatformAnalytics, getAuthToken } from '../api';
import { useDateRange } from '../lib/dateFilter';
import { SuperAdminSidebar } from './SuperAdminSidebar';
import { SuperAdminDateBar } from './SuperAdminDateBar';
import { OverviewSection } from './sections/OverviewSection';
import { TenantsSection } from './sections/TenantsSection';
import { BillingSection } from './sections/BillingSection';
import { PlansSection } from './sections/PlansSection';
import { TrialsSection } from './sections/TrialsSection';
import { UsageSection } from './sections/UsageSection';
import { AccessSection } from './sections/AccessSection';
import { DeletionRequestsSection } from './sections/DeletionRequestsSection';
import { ReportsSection } from './sections/ReportsSection';
import { confirmPermanentDelete, isActiveSubscriber, isAccessManagedTenant } from './shared';
import type { SuperAdminContext, SuperAdminSection } from './types';
import { formatLocalYmd } from '../lib/loanScheduleDates';

const SECTION_TITLES: Record<SuperAdminSection, string> = {
  overview: 'Command center',
  tenants: 'Tenant registry',
  billing: 'Billing overview',
  plans: 'Plans & pricing',
  trials: 'Free trials',
  usage: 'Usage & storage',
  access: 'Access control',
  deletions: 'Deletion requests',
  reports: 'Reports & exports',
};

/** Local calendar YYYY-MM-DD — never toISOString (shifts a day in IST). */
function toQueryDate(d: Date | null): string | undefined {
  if (!d) return undefined;
  const ymd = formatLocalYmd(d);
  return ymd || undefined;
}

function parseDateInput(v: string): Date | null {
  const m = v.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0, 0);
}

export default function SuperAdminApp() {
  const navigate = useNavigate();
  // All-time by default; wall-clock (not shop "as of") so SMS cost is not filtered to ₹0.
  const date = useDateRange('all', { useWorkspaceAsOf: false });
  const [section, setSection] = useState<SuperAdminSection>('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [orgs, setOrgs] = useState<OrganizationPublic[]>([]);
  const [serverAnalytics, setServerAnalytics] = useState<PlatformAnalytics | null>(null);
  const [loadError, setLoadError] = useState('');
  const [reloading, setReloading] = useState(false);
  const [selectedOrgUsage, setSelectedOrgUsage] = useState<AdminOrgUsage | null>(null);
  const [tenantSummaries, setTenantSummaries] = useState<AdminTenantUsageSummary[]>([]);
  const [usageLoadingFor, setUsageLoadingFor] = useState('');
  const [holdTarget, setHoldTarget] = useState<OrganizationPublic | null>(null);
  const [holdReason, setHoldReason] = useState('');

  const dateParams = useMemo(
    () => ({ from: toQueryDate(date.range.start), to: toQueryDate(date.range.end) }),
    [date.range.start, date.range.end]
  );

  const reload = useCallback(async () => {
    setReloading(true);
    try {
      const [orgRows, analytics, tenantUsage] = await Promise.all([
        fetchOrganizationsForAdmin(dateParams),
        adminApi.analytics(dateParams).catch(() => null),
        adminApi.listTenantUsage(dateParams).catch(() => ({ tenants: [] })),
      ]);
      setOrgs(orgRows);
      setServerAnalytics(analytics);
      setTenantSummaries(tenantUsage.tenants);
      setLoadError('');
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load platform data.');
    } finally {
      setReloading(false);
    }
  }, [dateParams]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    const jwt = getAuthToken();
    if (!token || role !== 'SUPER_ADMIN' || !token.startsWith('super-frontend-token') || !jwt) {
      navigate('/login', { replace: true });
      return;
    }
    void reload();
  }, [navigate, reload]);

  // Refresh open tenant SMS drill-down when the global date bar changes.
  useEffect(() => {
    const orgId = selectedOrgUsage?.organization?.id;
    if (!orgId) return;
    let cancelled = false;
    (async () => {
      setUsageLoadingFor(orgId);
      try {
        const next = await adminApi.orgUsage(orgId, { ...dateParams, sort: 'date_desc', limit: 50 });
        if (!cancelled) setSelectedOrgUsage(next);
      } catch {
        /* keep previous panel */
      } finally {
        if (!cancelled) setUsageLoadingFor('');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-fetch when dateParams change
  }, [dateParams.from, dateParams.to]);

  const activeOrgs = useMemo(() => orgs.filter((o) => isActiveSubscriber(o.subscriptionStatus)), [orgs]);
  const accessOrgs = useMemo(() => orgs.filter((o) => isAccessManagedTenant(o.subscriptionStatus)), [orgs]);
  const activeTenantSummaries = useMemo(
    () => tenantSummaries.filter((t) => isActiveSubscriber(t.subscriptionStatus)),
    [tenantSummaries]
  );

  const fallbackSmsTotal = activeOrgs.reduce((s, o) => s + (o.whatsappMessagesInPeriod ?? 0), 0);
  const analytics: PlatformAnalytics = serverAnalytics || {
    total: orgs.length,
    activeBilling: activeOrgs.length,
    pending: orgs.filter((o) => o.subscriptionStatus === 'pending_payment').length,
    pastDue: orgs.filter((o) => o.subscriptionStatus === 'past_due').length,
    onHold: orgs.filter((o) => o.accountHold).length,
    signups7d: 0,
    signups30d: 0,
    activeLast14d: 0,
    signupsInPeriod: 0,
    activeInPeriod: 0,
    totalDataKeys: activeOrgs.reduce((s, o) => s + (o.dataKeys ?? 0), 0),
    planMix: activeOrgs.reduce<Record<string, number>>((mix, o) => {
      const key = o.planId || '—';
      mix[key] = (mix[key] || 0) + 1;
      return mix;
    }, {}),
    smsMessagesTotal: fallbackSmsTotal,
    smsApiCostInr: Number((fallbackSmsTotal * 0.25).toFixed(2)),
    smsUnitCostInr: 0.25,
    whatsappMessagesTotal: fallbackSmsTotal,
    whatsappMessages30d: 0,
    whatsappMessages7d: 0,
    whatsappApiCostInr: Number((fallbackSmsTotal * 0.25).toFixed(2)),
  };

  const ctx: SuperAdminContext = {
    orgs,
    activeOrgs,
    accessOrgs,
    analytics,
    dateRange: date.range,
    dateLabel: date.label,
    loadError,
    reloading,
    reload,
    selectedOrgUsage,
    usageLoadingFor,
    tenantSummaries,
    activeTenantSummaries,
    loadOrgUsage: async (org) => {
      setUsageLoadingFor(org.id);
      try {
        setSelectedOrgUsage(await adminApi.orgUsage(org.id, { ...dateParams, sort: 'date_desc', limit: 50 }));
      } catch (e) {
        window.alert(e instanceof Error ? e.message : 'Failed to load tenant usage.');
      } finally {
        setUsageLoadingFor('');
      }
    },
    openTenantUsage: async (org) => {
      setSection('usage');
      setSidebarOpen(false);
      setUsageLoadingFor(org.id);
      try {
        setSelectedOrgUsage(await adminApi.orgUsage(org.id, { ...dateParams, sort: 'date_desc', limit: 50 }));
      } catch (e) {
        window.alert(e instanceof Error ? e.message : 'Failed to load tenant usage.');
      } finally {
        setUsageLoadingFor('');
      }
    },
    clearOrgUsage: () => setSelectedOrgUsage(null),
    onPlaceHold: (org) => {
      setHoldReason('');
      setHoldTarget(org);
    },
    onReleaseHold: async (org) => {
      if (!window.confirm(`Release access hold for "${org.name}"?`)) return;
      try {
        await setOrganizationAccountHoldRemote(org.id, false);
        await reload();
      } catch (e) {
        window.alert(e instanceof Error ? e.message : 'Failed to release hold.');
      }
    },
    onDeleteOrg: async (org) => {
      if (!confirmPermanentDelete('tenant workspace', org.name)) return;
      try {
        const res = await adminApi.deleteOrg(org.id);
        if (selectedOrgUsage?.organization.id === org.id) {
          setSelectedOrgUsage(null);
        }
        await reload();
        window.alert(`Deleted "${res.orgName || org.name}" from the database.`);
      } catch (e) {
        window.alert(e instanceof Error ? e.message : 'Failed to delete tenant.');
      }
    },
  };

  const applyHold = async () => {
    if (!holdTarget) return;
    const r = holdReason.trim();
    if (r.length < 4) {
      window.alert('Enter a short reason (at least 4 characters) for the audit trail.');
      return;
    }
    try {
      await setOrganizationAccountHoldRemote(holdTarget.id, true, r);
      setHoldTarget(null);
      setHoldReason('');
      await reload();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Failed to place hold.');
    }
  };

  const renderSection = () => {
    switch (section) {
      case 'overview':
        return <OverviewSection ctx={ctx} />;
      case 'tenants':
        return <TenantsSection ctx={ctx} />;
      case 'billing':
        return <BillingSection ctx={ctx} />;
      case 'plans':
        return <PlansSection />;
      case 'trials':
        return <TrialsSection ctx={ctx} />;
      case 'usage':
        return <UsageSection ctx={ctx} />;
      case 'access':
        return <AccessSection ctx={ctx} />;
      case 'deletions':
        return <DeletionRequestsSection onChanged={() => void reload()} />;
      case 'reports':
        return <ReportsSection ctx={ctx} />;
      default:
        return <OverviewSection ctx={ctx} />;
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-zinc-100 font-sans text-zinc-900">
      {sidebarOpen ? (
        <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} aria-hidden />
      ) : null}

      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-200 lg:relative lg:z-30 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <SuperAdminSidebar
          active={section}
          onNavigate={setSection}
          onLogout={() => {
            clearAuthAndTenant();
            navigate('/login');
          }}
          onCloseMobile={() => setSidebarOpen(false)}
        />
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="border-b border-zinc-200 bg-white px-4 py-3 shadow-sm sm:px-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                className="rounded-lg border border-zinc-200 p-2 text-zinc-700 hover:bg-zinc-50 lg:hidden"
              >
                <Menu className="h-5 w-5" />
              </button>
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-600">{PRODUCT_NAME} platform</p>
                <h1 className="truncate text-lg font-semibold text-zinc-900">{SECTION_TITLES[section]}</h1>
              </div>
            </div>
            <button
              type="button"
              onClick={() => void reload()}
              disabled={reloading}
              className="inline-flex items-center gap-2 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm font-semibold text-zinc-800 hover:bg-zinc-100 disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${reloading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="mx-auto max-w-[1440px] space-y-5">
            <SuperAdminDateBar
              preset={date.preset}
              start={date.range.start ? formatLocalYmd(date.range.start) : ''}
              end={date.range.end ? formatLocalYmd(date.range.end) : ''}
              label={date.label}
              onPreset={(p) => date.setPreset(p)}
              onStart={(v) => date.setStart(v ? parseDateInput(v) : null)}
              onEnd={(v) => date.setEnd(v ? parseDateInput(v) : null)}
            />

            {loadError ? (
              <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900">{loadError}</div>
            ) : null}

            {renderSection()}
          </div>
        </main>
      </div>

      {holdTarget ? (
        <div className="fixed inset-0 z-[60] flex items-end justify-center bg-zinc-950/60 p-4 sm:items-center">
          <div className="w-full max-w-md rounded-2xl border border-zinc-200 bg-white p-5 shadow-2xl">
            <h3 className="text-base font-semibold text-zinc-900">Place access hold</h3>
            <p className="mt-1 text-sm text-zinc-600">
              <strong>{holdTarget.name}</strong> — tenant admins will be redirected to the hold page until you release.
            </p>
            <label className="mt-4 block text-xs font-semibold uppercase tracking-wide text-zinc-500">Reason (audit)</label>
            <textarea
              value={holdReason}
              onChange={(e) => setHoldReason(e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm focus:border-rose-400/60 focus:bg-white focus:outline-none focus:ring-2 focus:ring-rose-500/15"
              placeholder="e.g. Invoice #1042 unpaid past 14 days"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setHoldTarget(null);
                  setHoldReason('');
                }}
                className="rounded-lg border border-zinc-200 px-4 py-2 text-sm font-semibold text-zinc-700 hover:bg-zinc-50"
              >
                Cancel
              </button>
              <button type="button" onClick={() => void applyHold()} className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700">
                Confirm hold
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
